import { Anchor, Button, Card, Grid, Group, Modal, Stack, Table, Text, Title } from '@mantine/core';
import { IconEdit, IconUserPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import type { ExporterDto, Paginated, UserDto } from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { AccountStatusBadge } from '@/components/badges';
import { PageHeader } from '@/components/PageHeader';
import { SecretRevealModal } from '@/components/SecretReveal';
import { ErrorState, LoadingState } from '@/components/States';
import { drKeys } from '@/features/requests/api';
import { BdsReviewerSelect } from '@/features/requests/components/RequestFields';
import { UserDrawer } from '@/features/users/UserDrawer';
import { formatDate, fromNow } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';
import { ExporterDrawer } from './ExporterDrawer';

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text fz="xs" c="dimmed" tt="uppercase" fw={600} lts={0.3}>
        {label}
      </Text>
      <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {children || '—'}
      </Text>
    </Stack>
  );
}

/** Who looks after the client in BDS: its portal requisitions, earlier ones included, show to that SC-NBD user's team. */
function BdContactModal({ exporter, opened, onClose }: { exporter: ExporterDto; opened: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(exporter.bdContact?.id ?? null);
  useEffect(() => {
    if (opened) setUserId(exporter.bdContact?.id ?? null);
  }, [opened, exporter.bdContact?.id]);
  const save = useMutation({
    mutationFn: (value: string | null) => http.put<ExporterDto>(`/exporters/${exporter.id}/bd-contact`, { userId: value }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['exporters', 'detail', exporter.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
      void queryClient.invalidateQueries({ queryKey: drKeys.all });
      notifySuccess(updated.bdContact ? `${updated.bdContact.name} is the BD contact for ${updated.companyName}.` : 'BD contact removed.');
      onClose();
    },
    onError: (error) => notifyError(error),
  });
  return (
    <Modal opened={opened} onClose={onClose} title="BD contact in BDS">
      <Stack>
        <Text fz="sm" c="dimmed">
          {exporter.companyName}'s portal requisitions, including the ones already raised, show in BDS to this SC-NBD user's team. Without a contact, every
          SC-NBD user sees them.
        </Text>
        {opened && <BdsReviewerSelect value={userId} onChange={setUserId} label="BD contact (BDS)" />}
        <Group justify="space-between">
          {exporter.bdContact ? (
            <Button variant="subtle" color="red" loading={save.isPending && save.variables === null} onClick={() => save.mutate(null)}>
              Remove contact
            </Button>
          ) : (
            <span />
          )}
          <Group gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!userId || userId === exporter.bdContact?.id} loading={save.isPending && save.variables !== null} onClick={() => save.mutate(userId)}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export function ExporterDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [pickingContact, setPickingContact] = useState(false);
  const [addingLogin, setAddingLogin] = useState(false);
  const [secret, setSecret] = useState<{ email: string; value: string } | null>(null);

  const exporter = useQuery({ queryKey: ['exporters', 'detail', id], queryFn: () => http.get<ExporterDto>(`/exporters/${id}`) });
  const logins = useQuery({
    queryKey: ['users', 'list', { exporterId: id }],
    queryFn: () => http.get<Paginated<UserDto>>('/users', { exporterId: id, limit: 100 }),
    enabled: can('Users', 'view'),
  });

  if (exporter.isLoading) return <LoadingState />;
  if (exporter.isError || !exporter.data) return <ErrorState error={exporter.error} onRetry={() => void exporter.refetch()} />;
  const e = exporter.data;

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Exporters', to: '/internal/exporters' }, { label: e.companyName }]}
        title={e.companyName}
        badge={<AccountStatusBadge status={e.status} />}
        description={<Text span ff="monospace" fz="sm">{e.code}</Text>}
        actions={
          can('Exporters', 'edit') && (
            <Button variant="light" leftSection={<IconEdit size={16} />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        }
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <Title order={4}>Company details</Title>
              <Detail label="Contact person">{e.contactPerson}</Detail>
              <Detail label="Email">{e.email && <Anchor href={`mailto:${e.email}`}>{e.email}</Anchor>}</Detail>
              <Detail label="Phone">{e.phone}</Detail>
              <Detail label="Country">{e.country}</Detail>
              <Detail label="Address">{e.address}</Detail>
              <Detail label="Website">{e.website}</Detail>
              <Detail label="Notes">{e.notes}</Detail>
              <Stack gap={2}>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Text fz="xs" c="dimmed" tt="uppercase" fw={600} lts={0.3}>
                    BD contact (BDS)
                  </Text>
                  {can('Exporters', 'edit') && (
                    <Button size="compact-xs" variant="subtle" onClick={() => setPickingContact(true)}>
                      {e.bdContact ? 'Change' : 'Choose'}
                    </Button>
                  )}
                </Group>
                <Text fz="sm">{e.bdContact ? `${e.bdContact.name}${e.bdContact.teamName ? ` · ${e.bdContact.teamName}` : ''}` : 'None'}</Text>
                <Text fz="xs" c="dimmed">
                  {e.bdContact
                    ? "This client's portal requisitions show in BDS to this person's SC-NBD team."
                    : "Every SC-NBD user sees this client's portal requisitions in BDS until a contact is chosen."}
                </Text>
              </Stack>
              <Text fz="xs" c="dimmed">
                Added {formatDate(e.createdAt)} · updated {fromNow(e.updatedAt)}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
        {can('Users', 'view') && (
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card>
              <Group justify="space-between" mb="md">
                <Title order={4}>Portal logins</Title>
                {can('Users', 'edit') && (
                  <Button size="xs" variant="light" leftSection={<IconUserPlus size={14} />} onClick={() => setAddingLogin(true)}>
                    Add login
                  </Button>
                )}
              </Group>
              <Text c="dimmed" fz="xs" mb="sm">
                Clients sign in at{' '}
                <Text span ff="monospace" fz="xs">
                  {window.location.origin}/login
                </Text>{' '}
                to raise document requests for this company and download approved documents. Only administrators create logins; share the
                password with the client yourself.
              </Text>
              {logins.isLoading ? (
                <LoadingState />
              ) : !logins.data?.items.length ? (
                <Text c="dimmed" fz="sm">
                  No one from this company can sign in yet. Add a login so they can request documents in the client portal.
                </Text>
              ) : (
                <Table>
                  <Table.Tbody>
                    {logins.data.items.map((u) => (
                      <Table.Tr key={u.id}>
                        <Table.Td>
                          <Text fz="sm" fw={600}>
                            {u.fullName}
                          </Text>
                          <Text fz="xs" c="dimmed">
                            {u.email}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <AccountStatusBadge status={u.status} />
                        </Table.Td>
                        <Table.Td>
                          <Text fz="xs" c="dimmed">
                            {u.lastLoginAt ? `Signed in ${fromNow(u.lastLoginAt)}` : 'Never signed in'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          </Grid.Col>
        )}
      </Grid>
      <ExporterDrawer opened={editing} exporter={e} onClose={() => setEditing(false)} onSaved={() => notifySuccess('Exporter updated.')} />
      <BdContactModal exporter={e} opened={pickingContact} onClose={() => setPickingContact(false)} />
      <UserDrawer
        opened={addingLogin}
        presetExporterId={e.id}
        onClose={() => setAddingLogin(false)}
        onCreated={(res) => {
          if (res.temporaryPassword) setSecret({ email: res.user.email, value: res.temporaryPassword });
          else notifySuccess('Login created.');
        }}
      />
      <SecretRevealModal opened={!!secret} onClose={() => setSecret(null)} title="Login created" email={secret?.email ?? ''} secret={secret?.value ?? null} />
    </>
  );
}
